import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { ActivityIndicator, View } from 'react-native'

import { Button, type TDefaultButtonProps } from '@/src/elements/button/default'
import { Placeholder } from '@/src/elements/placeholder'
import { COMMUNITY_ACTIVITY, COMMUNITY_LIFETIME } from '@/src/features/community/models/community.constants'
import { useCommunitySubscriptionsQuery } from '@/src/features/community/services/useCommunitySubscriptionsQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import type { PUBLICATION_TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import {
  type TUseCommunitySubscriptionRouteProps,
  useCommunitySubscriptionRoute,
} from '../../../hooks/useCommunitySubscriptionRoute'

const ActionButtonWrapper = ({ children }: { children: ReactNode }) => (
  <View className="w-full absolute bottom-0 p-6 bg-transparent flex flex-row items-center justify-center">
    {children}
  </View>
)

const BaseCommunityActionButton = (props: TDefaultButtonProps) => (
  <ActionButtonWrapper>
    <Button shadow="xl" colors="primary" variant="solid" className="w-full md:max-w-[760px]" {...props} />
  </ActionButtonWrapper>
)

const CommunitySubscriptionButton = ({
  children,
  ...otherProps
}: TUseCommunitySubscriptionRouteProps & { children: ReactNode }) => {
  const router = useRouter()
  const subscriptionRoute = useCommunitySubscriptionRoute(otherProps)

  return (
    <BaseCommunityActionButton onPress={() => router.navigate(subscriptionRoute)}>{children}</BaseCommunityActionButton>
  )
}

type TCommunityActionButtonProps = {
  communityId: number
  community: Omit<TUseCommunitySubscriptionRouteProps, 'course' | 'source' | 'origin'> | undefined
  publicationTrackingSource: PUBLICATION_TRACKING_SOURCE
} & Pick<TUseCommunitySubscriptionRouteProps, 'course' | 'source' | 'origin'>

export const CommunityActionButton = ({ communityId, community, ...otherProps }: TCommunityActionButtonProps) => {
  const router = useRouter()
  const { routes } = useRoutes()
  const {
    data: subscriptions,
    activeSubscription,
    isError,
    isLoading,
  } = useCommunitySubscriptionsQuery(communityId, community?.lifetime)

  if (isError) {
    return null
  }

  if (isLoading || !community) {
    return (
      <ActionButtonWrapper>
        <View className="w-full md:max-w-[760px] shadow-xl rounded-full h-14 bg-default-bg-loading">
          <Placeholder contrast="dark" className="w-full h-full justify-end" testID={TEST_ID.MISCELLANEOUS.PLACEHOLDER}>
            <ActivityIndicator color="black" />
          </Placeholder>
        </View>
      </ActionButtonWrapper>
    )
  }

  const isSubscriptionExpired = activeSubscription?.toDate && dayjs().isAfter(activeSubscription.toDate, 'day')

  if (!activeSubscription || (isSubscriptionExpired && activeSubscription.finishedAt)) {
    const hasAnySubscription = subscriptions && subscriptions.length > 0

    return (
      <CommunitySubscriptionButton {...community} {...otherProps}>
        <FormattedMessage id={hasAnySubscription ? 'word.start-again' : 'words.sign-up'} />
      </CommunitySubscriptionButton>
    )
  }

  const isSubscriptionUnstarted = activeSubscription.fromDate && dayjs().isBefore(activeSubscription.fromDate, 'day')

  if (isSubscriptionUnstarted && community.activity === COMMUNITY_ACTIVITY.COMING_SOON) {
    return null
  }

  const nextPublication = activeSubscription.nextPublication

  if (!nextPublication || activeSubscription.isPrayedToday) {
    return (
      <BaseCommunityActionButton onPress={() => router.navigate(routes['prayer-space']())}>
        <FormattedMessage id="community.close-for-today" />
      </BaseCommunityActionButton>
    )
  }

  const goToPublication = () => router.push(routes.publicationById({ id: nextPublication.id, ...otherProps }))

  if (community.lifetime === COMMUNITY_LIFETIME.EVERLASTING) {
    return (
      <BaseCommunityActionButton startIcon={ICON.PLAY} onPress={goToPublication}>
        <FormattedMessage id="pray.pray-today" />
      </BaseCommunityActionButton>
    )
  }

  const hasStarted = nextPublication.rank !== 1

  return (
    <BaseCommunityActionButton startIcon={ICON.PLAY} onPress={goToPublication}>
      {hasStarted ? <FormattedMessage id="word.continue" /> : <FormattedMessage id="word.start" />}{' '}
      <FormattedMessage id="day.count" values={{ number: nextPublication?.rank || 1 }} />
    </BaseCommunityActionButton>
  )
}
